import { motion } from "framer-motion";
import { Seo } from "@/seo/Seo";
import { PageHero } from "@/layouts/PageHero";
import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { SectionHeading } from "@/components/common/SectionHeading";
import { CtaButton } from "@/components/common/CtaButton";
import { DetailCta } from "@/components/detail/DetailCta";
import { techDisciplines } from "@/data/technologies.data";
import { fadeUp, staggerContainer, viewportOnce } from "@/animations/motion";
import { Layers, MapPin, TrendingUp } from "@/icons";
import { siteConfig } from "@/config/site.config";

export default function Technologies() {
  return (
    <>
      <Seo
        title="Technologies"
        description="The technologies VibeWell uses across frontend, backend, mobile, cloud, data, DevOps and AI — and why we choose each, where they're used and the business benefit."
        path="/technologies"
      />
      <PageHero
        eyebrow="Technologies"
        titleLines={["A modern stack,", "chosen deliberately."]}
        description="We don't chase hype. We choose tools for reliability, speed and longevity — here's what powers what we build, and why it matters for your business."
      >
        <CtaButton to={siteConfig.primaryCta.path} withArrow magnetic>
          Book a Free Consultation
        </CtaButton>
      </PageHero>

      <Section spacing="compact" className="pt-0">
        <Container>
          <SectionHeading
            eyebrow="Our philosophy"
            title="The right tool for the job — not the trend"
            description="Every technology on this page earns its place by making your product faster, safer or cheaper to run and evolve."
          />

          <motion.div
            variants={staggerContainer(0.08, 0.05)}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="mt-12 grid gap-6 lg:grid-cols-2"
          >
            {techDisciplines.map(({ category, icon: Icon, rationale, whereUsed, benefit, items }) => (
              <motion.div
                key={category}
                variants={fadeUp}
                data-testid={`tech-discipline-${category.toLowerCase()}`}
                className="group flex flex-col rounded-[1.5rem] border border-border bg-card p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary/20 hover:shadow-[0_24px_60px_-30px_rgba(15,23,42,0.24)]"
              >
                <div className="flex items-center gap-4">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="font-heading text-xl font-bold text-foreground">{category}</h3>
                </div>

                <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground">{rationale}</p>

                <div className="mt-5 space-y-3 border-t border-border pt-5">
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <p className="text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">Where we use it: </span>
                      {whereUsed}
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <p className="text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">Business benefit: </span>
                      {benefit}
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  {items.map((item) => (
                    <span
                      key={item}
                      className="rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      <DetailCta note="Curious whether your stack is set up to scale? Let's take a look together." />
    </>
  );
}
